import { useNavigate, useParams } from 'react-router-dom';

import { BuildingBlocks } from '@/components/elements/building-blocks/building-blocks.component';
import TailoringLayout from '@/components/layout/tailoring/tailoring.layout.component';
import { DesignObligation, LifecycleStatus, Product, ProductBuildingBlock } from '@/gql/graphql';
import { useQuery } from '@apollo/client';
import { getProduct } from '../pre-tailoring/pre-tailoring.requests';

import { T } from '@/components/i18n/message.i18n.component';

import { TailoringHeading } from '@/components/elements/tailoring-heading/tailoring-heading.component';
import { useBuildingBlocks } from '@/hooks/use-building-blocks/use-building-blocks.hook';
import { GroupedBuildingBlock } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { useDialogStore } from '@/store/dialog.store';
import { BBSelection } from './bb-selection/bb-selection.component';
import { TailorBuildingBlockForm } from './forms/tailor-bb.form';
import { TailorDesignObligationForm } from './forms/tailor-do.form';
import { tailorFormsTranslations } from './tailoring-forms.messages';
import { tailoringMessages } from './tailoring.messages';
import { inclusionBBSelection, isSelectedBBIncluded } from './tailoring.page.core';

export const TailoringPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { openDialog } = useDialogStore((state) => state);

  const { data, loading } = useQuery<{ product: Product }>(getProduct, {
    variables: { id: id || '' },
  });

  const {
    selectedBuildingBlock: selectedBB,
    includedBuildingBlocks,
    excludedBuildingBlocks,
    sort,
    setSelectedBuildingBlock: setSelectedBB,
    handleSortChange: onSortChange,
  } = useBuildingBlocks({
    buildingBlocks: data?.product.tailoringProcess.buildingBlocks,
  });

  const handleBack = () => {
    navigate(`/product/${id}/pre-tailoring`);
  };
  const handleExport = () => {};
  const handleToProducts = () => {
    navigate('/products');
  };

  const handleBlockClick = (bb: ProductBuildingBlock) => {
    setSelectedBB(bb);
  };

  const handleTabChange = (context: string) => {
    if (context === 'included' && includedBuildingBlocks.data.length > 0) {
      setSelectedBB(includedBuildingBlocks.data[0].buidingBlocks[0]);
    } else if (context === 'excluded' && excludedBuildingBlocks.data.length > 0) {
      setSelectedBB(excludedBuildingBlocks.data[0].buidingBlocks[0]);
    }
  };

  const onSelectBB = (bb: ProductBuildingBlock | undefined) => {
    setSelectedBB(bb);
  };

  const onBBInclusion = (bb: ProductBuildingBlock) => {
    openDialog({
      type: 'custom',
      title: isSelectedBBIncluded(bb) ? (
        <T {...tailorFormsTranslations.bbTailroingFormTitleExclude} />
      ) : (
        <T {...tailorFormsTranslations.bbTailroingFormTitleInclude} />
      ),

      element: (
        <TailorBuildingBlockForm
          productId={data?.product.id}
          bb={bb}
          afterAdd={(payload) => {
            const currentBuildingBlocks = isSelectedBBIncluded(bb) ? includedBuildingBlocks : excludedBuildingBlocks;
            const includedBB = currentBuildingBlocks.data.find((group) => {
              return group.buidingBlocks.find((b) => b.id === bb.id);
            });

            if (!includedBB) return;
            setSelectedBB(inclusionBBSelection(includedBB, [payload], currentBuildingBlocks));
          }}
        />
      ),
    });
  };

  const handleIncludeExcludeDO = (groupedBB: GroupedBuildingBlock<DesignObligation>, including: boolean) => {
    const translation = including
      ? tailoringMessages.includeDesignObligation
      : tailoringMessages.excludeDesignObligation;

    openDialog({
      type: 'custom',
      fullScreen: true,
      title: <T {...translation} />,
      element: (
        <TailorDesignObligationForm
          includedBB={including}
          groupedBB={groupedBB}
          afterAdd={(bbs) => {
            if (!setSelectedBB) return;
            if (including) {
              setSelectedBB(inclusionBBSelection(groupedBB, bbs, excludedBuildingBlocks));
            } else {
              setSelectedBB(inclusionBBSelection(groupedBB, bbs, includedBuildingBlocks));
            }
          }}
        />
      ),
    });
  };

  if (!data?.product) return null;

  return (
    <TailoringLayout
      onToProducts={handleToProducts}
      onNext={handleExport}
      onBack={handleBack}
      loading={loading}
      title={<TailoringHeading context="tailoring" product={data?.product} />}
      left={
        <BBSelection
          selectedBB={selectedBB}
          handleBBInclusion={onBBInclusion}
          isProductActive={data?.product.lifecycleStatus === LifecycleStatus.Active}
        />
      }
      right={
        <BuildingBlocks
          sort={sort}
          handleSortChange={onSortChange}
          isProductActive={data?.product.lifecycleStatus === LifecycleStatus.Active}
          includedBuildingBlocks={includedBuildingBlocks}
          excludedBuildingBlocks={excludedBuildingBlocks}
          onTabChange={handleTabChange}
          onBBInclusionChange={handleIncludeExcludeDO}
          selectedBuildingBlockId={selectedBB?.id}
          onBlockClick={handleBlockClick}
          setSelectedBB={onSelectBB}
        />
      }
    />
  );
};
